import React from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import { Button, Input, LinearStepper, Modal, Select } from "../ui";
import {
  disconnectEmailIntegration,
  saveAndTestEmailIntegration,
  type EmailIntegrationStatus,
} from "../../utils";
import {
  EMAIL_CONFIGURE_STEP_FIELDS,
  SMTP_PORT_OPTIONS,
  emailConfigureInitialValues,
  emailConfigureSchema,
  type EmailConfigureFormValues,
  type EmailProvider,
} from "../../validators/emailIntegrationValidators";

const STEPS = [
  { id: "provider", label: "Provider" },
  { id: "credentials", label: "Credentials" },
  { id: "test", label: "Test send" },
];

const PROVIDERS: Array<{
  value: EmailProvider;
  title: string;
  description: string;
}> = [
  {
    value: "smtp",
    title: "SMTP",
    description: "Gmail, Outlook, or any mail server. Host, port, username, and password.",
  },
  {
    value: "sendgrid",
    title: "SendGrid",
    description: "API key plus a verified sender address from your SendGrid account.",
  },
];

const outlineBtnClass =
  "h-10 !border-line px-3.5 font-semibold text-dark-blue hover:bg-paper";
const primaryBtnClass = "h-10 px-3.5 font-semibold text-dark-blue";

type EmailConfigureModalProps = {
  isOpen: boolean;
  onClose: () => void;
  defaultTestEmail?: string;
  existing?: EmailIntegrationStatus | null;
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
  onUpdated: (status: EmailIntegrationStatus) => void;
};

const valuesFromExisting = (
  existing: EmailIntegrationStatus | null | undefined,
  defaultTestEmail: string
): EmailConfigureFormValues => {
  const base = emailConfigureInitialValues(defaultTestEmail);
  if (!existing?.configured || !existing.provider) return base;
  return {
    ...base,
    provider: existing.provider,
    fromName: existing.fromName || "",
    fromEmail: existing.fromEmail || "",
    smtpHost: existing.smtpHost || "",
    smtpPort: existing.smtpPort || "587",
    smtpUser: existing.smtpUser || "",
    keepExistingSecret: Boolean(existing.hasSecret),
  };
};

const EmailConfigureModal = ({
  isOpen,
  onClose,
  defaultTestEmail = "",
  existing = null,
  ownerId,
  userId,
  workspaceId,
  onUpdated,
}: EmailConfigureModalProps) => {
  const [step, setStep] = React.useState(0);
  const [isDisconnecting, setIsDisconnecting] = React.useState(false);

  const formik = useFormik({
    initialValues: valuesFromExisting(existing, defaultTestEmail),
    validationSchema: emailConfigureSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      if (values.provider !== "smtp" && values.provider !== "sendgrid") return;
      try {
        const result = await saveAndTestEmailIntegration({
          ownerId,
          userId,
          workspaceId,
          provider: values.provider,
          fromName: values.fromName.trim(),
          fromEmail: values.fromEmail.trim(),
          smtpHost: values.smtpHost.trim(),
          smtpPort: values.smtpPort,
          smtpUser: values.smtpUser.trim(),
          smtpPassword: values.smtpPassword,
          sendgridApiKey: values.sendgridApiKey.trim(),
          testTo: values.testTo.trim(),
        });
        onUpdated(result.integration);
        if (result.ok) {
          toast.success(result.message || "Test email delivered.");
          onClose();
          return;
        }
        toast.error(result.message || "Test send failed. Check your credentials.");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Test send failed.");
      }
    },
  });

  React.useEffect(() => {
    if (!isOpen) return;
    setStep(0);
    formik.resetForm({ values: valuesFromExisting(existing, defaultTestEmail) });
    // Hydrate from the latest saved config only when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const showError = (field: keyof typeof formik.values) =>
    (formik.touched[field] || formik.submitCount > 0) &&
    typeof formik.errors[field] === "string"
      ? String(formik.errors[field])
      : "";

  const goNext = async () => {
    const fields = EMAIL_CONFIGURE_STEP_FIELDS[step] || [];
    const errors = await formik.validateForm();
    const nextTouched = { ...formik.touched };
    fields.forEach((field) => {
      nextTouched[field] = true;
    });
    formik.setTouched(nextTouched, false);

    const stepHasError = fields.some((field) => Boolean(errors[field]));
    if (stepHasError) return;
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const goBack = () => setStep((current) => Math.max(current - 1, 0));

  const selectProvider = (nextProvider: EmailProvider) => {
    const keep =
      Boolean(existing?.configured) &&
      existing?.provider === nextProvider &&
      Boolean(existing?.hasSecret);
    void formik.setFieldValue("provider", nextProvider, true);
    void formik.setFieldValue("keepExistingSecret", keep, false);
    void formik.setFieldTouched("provider", true, false);
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const status = await disconnectEmailIntegration({ ownerId, userId, workspaceId });
      onUpdated(status);
      toast.success("Email server disconnected.");
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to disconnect email.");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const providerLabel =
    formik.values.provider === "sendgrid"
      ? "SendGrid"
      : formik.values.provider === "smtp"
        ? "SMTP"
        : "";
  const secretHint = formik.values.keepExistingSecret
    ? "Leave blank to keep the saved secret."
    : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="small"
      title="Configure email server"
      closeIconSrc="/icons/x_circle.svg"
      panelClassName="bg-white p-4"
      headerClassName="rounded-t-[12px] border border-border-grey border-b-0 bg-white px-4 pt-3"
      titleClassName="text-md !font-medium"
      bodyClassName="rounded-b-[12px] border border-t-0 border-border-grey bg-white px-4 py-4"
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (step < STEPS.length - 1) {
            void goNext();
            return;
          }
          void formik.handleSubmit();
        }}
      >
        <p className="text-sm text-muted">
          Save credentials, then send a test. Campaign email actions stay unavailable until that
          test succeeds.
        </p>

        <LinearStepper
          steps={STEPS}
          currentStep={step}
          maxNavigableStep={step}
          onStepClick={(index) => {
            if (index <= step) setStep(index);
          }}
        />

        {step === 0 ? (
          <div className="space-y-2">
            {PROVIDERS.map((option) => {
              const selected = formik.values.provider === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => selectProvider(option.value)}
                  aria-pressed={selected}
                  className={`w-full rounded-[8px] border p-3 text-left transition-colors ${
                    selected
                      ? "border-mainColor bg-notice-bg"
                      : "border-line bg-white hover:bg-paper"
                  }`}
                >
                  <p className="text-sm font-semibold text-dark-blue">{option.title}</p>
                  <p className="mt-1 text-[11px] text-muted">{option.description}</p>
                </button>
              );
            })}
            {showError("provider") ? (
              <p className="text-xs text-red-600">{showError("provider")}</p>
            ) : null}
          </div>
        ) : null}

        {step === 1 ? (
          <div className="space-y-3">
            <Input
              label="From name"
              id="fromName"
              name="fromName"
              size="sm"
              placeholder="InnLead"
              value={formik.values.fromName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={showError("fromName")}
            />
            <Input
              label="From email"
              id="fromEmail"
              name="fromEmail"
              type="email"
              size="sm"
              placeholder="outreach@yourcompany.com"
              value={formik.values.fromEmail}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={showError("fromEmail")}
            />
            {formik.values.provider === "smtp" ? (
              <>
                <Input
                  label="SMTP host"
                  id="smtpHost"
                  name="smtpHost"
                  size="sm"
                  placeholder="smtp.gmail.com"
                  value={formik.values.smtpHost}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={showError("smtpHost")}
                />
                <Select
                  label="Port"
                  value={formik.values.smtpPort}
                  options={SMTP_PORT_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  onChange={(value) => formik.setFieldValue("smtpPort", value)}
                  size="sm"
                />
                <Input
                  label="Username"
                  id="smtpUser"
                  name="smtpUser"
                  size="sm"
                  placeholder="Your SMTP username"
                  value={formik.values.smtpUser}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={showError("smtpUser")}
                />
                <Input
                  label="Password"
                  id="smtpPassword"
                  name="smtpPassword"
                  type="password"
                  size="sm"
                  placeholder={secretHint || "App password or SMTP password"}
                  value={formik.values.smtpPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={showError("smtpPassword")}
                />
                <p className="text-[11px] text-muted">
                  Gmail and Google Workspace need an app password, not your normal account
                  password.
                </p>
              </>
            ) : (
              <Input
                label="SendGrid API key"
                id="sendgridApiKey"
                name="sendgridApiKey"
                type="password"
                size="sm"
                placeholder={secretHint || "SG...."}
                value={formik.values.sendgridApiKey}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={showError("sendgridApiKey")}
              />
            )}
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-3">
            <div className="rounded-[9px] border border-line bg-paper px-[15px] py-[13px]">
              <p className="text-sm font-semibold text-dark-blue">
                {providerLabel || "Email"} · {formik.values.fromName || "From name"}
              </p>
              <p className="mt-1 text-[11px] text-muted">{formik.values.fromEmail}</p>
            </div>
            <Input
              label="Send test to"
              id="testTo"
              name="testTo"
              type="email"
              size="sm"
              placeholder="you@yourcompany.com"
              value={formik.values.testTo}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={showError("testTo")}
            />
            {existing?.lastError && existing.status !== "verified" ? (
              <p className="text-xs text-red-600">{existing.lastError}</p>
            ) : (
              <p className="text-[11px] text-muted">
                InnLead must deliver this test before the Email card can show as verified.
              </p>
            )}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2 pt-1">
          {step > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={outlineBtnClass}
              onClick={goBack}
              isDisabled={formik.isSubmitting || isDisconnecting}
            >
              Back
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={outlineBtnClass}
              onClick={onClose}
              isDisabled={formik.isSubmitting || isDisconnecting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            size="sm"
            className={`flex-1 ${primaryBtnClass}`}
            isLoading={formik.isSubmitting}
            isDisabled={isDisconnecting}
          >
            {step < STEPS.length - 1 ? "Continue" : "Save and send test"}
          </Button>
        </div>
        {existing?.configured ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 px-0 font-semibold text-muted"
            onClick={() => void handleDisconnect()}
            isLoading={isDisconnecting}
            isDisabled={formik.isSubmitting}
          >
            Disconnect email server
          </Button>
        ) : null}
      </form>
    </Modal>
  );
};

export default EmailConfigureModal;
